import { BALANCE, MODULE_TYPES, NEED_KINDS, SHIP_CLASSES, type ModuleType, type NeedKind, type ShipClass } from "@station-keeping/sim";
import type * as Phaser from "phaser";
import { COLORS } from "./palette";
import type { PixelCanvas } from "./pixel";
import {
  bracketArt,
  FONT_CHARS,
  GLYPH_H,
  GLYPH_W,
  MODULE_ANCHOR,
  WEAR_SEGMENTS,
  flameArt,
  fontArt,
  highlightArt,
  moduleArt,
  needDotArt,
  pixelArt,
  planetArt,
  puffArt,
  scaffoldArt,
  shipArt,
  signArt,
  skyArt,
  starsArt,
  sunArt,
  wearArt,
  zoneEdgeArt,
  type HighlightKind,
  type PlanetKind,
  type SignKind,
  type SkyKind,
  type WearArtLevel,
  type ZoneEdgeKind,
} from "./sprites";

/** Noms des textures. */
export const TEX = {
  module: (type: ModuleType, level = 1) => `module-${type}-${level}`,
  lights: (type: ModuleType, level = 1) => `lights-${type}-${level}`,
  zoneEdge: (kind: ZoneEdgeKind, ring: number) => `zone-${kind}-${ring}`,
  highlight: (kind: HighlightKind) => `highlight-${kind}`,
  sign: (kind: SignKind) => `sign-${kind}`,
  ship: (shipClass: ShipClass) => `ship-${shipClass}`,
  shipLights: (shipClass: ShipClass) => `ship-lights-${shipClass}`,
  flame: (frame: 0 | 1) => `flame-${frame}`,
  dot: (kind: NeedKind) => `dot-${kind}`,
  scaffold: "scaffold",
  bracket: "bracket",
  puff: "puff",
  pixel: "pixel",
  stars: "stars",
  sun: "sun",
  sky: (kind: SkyKind) => `sky-${kind}`,
  planet: (kind: PlanetKind, width: number) => `planet-${kind}-${width}`,
  wear: (level: WearArtLevel, filled: number) => `wear-${level}-${filled}`,
  font: "font",
} as const;

export interface ModuleSpriteInfo {
  /** Silhouette, pour toucher un module au pixel près. */
  mask: PixelCanvas;
  /** Hauteur visible au-dessus du centre du sol (pixels d'art). */
  height: number;
  hasLights: boolean;
}

export interface ShipSpriteInfo {
  /** Silhouette, pour toucher un vaisseau au pixel près. */
  mask: PixelCanvas;
  width: number;
  height: number;
  nozzle: { x: number; y: number };
}

export interface ArtBook {
  /** Par type, un sprite par niveau d'amélioration (indice : niveau − 1). */
  modules: Record<ModuleType, ModuleSpriteInfo[]>;
  ships: Record<ShipClass, ShipSpriteInfo>;
}

const SIGNS: SignKind[] = ["stage1", "stage2", "stage3", "job", "off", "unpowered", "unstaffed"];
const HIGHLIGHTS: HighlightKind[] = ["build", "blocked", "cursor", "select"];

function firstOpaqueRow(canvas: PixelCanvas): number {
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) if (canvas.alphaAt(x, y) > 0) return y;
  }
  return canvas.height;
}

/** Niveaux dessinés pour un type : le noyau ne s'améliore pas. */
function levelsOf(type: ModuleType): number[] {
  const count = type === "core" ? 1 : BALANCE.upgrades.maxLevel;
  return Array.from({ length: count }, (_, index) => index + 1);
}

/** Le sprite d'un module à son niveau (le plus proche dessiné). */
export function moduleSprite(book: ArtBook, type: ModuleType, level: number): ModuleSpriteInfo {
  const sprites = book.modules[type];
  return sprites[Math.min(sprites.length, Math.max(1, level)) - 1] as ModuleSpriteInfo;
}

/** Les sprites, dessinés une fois par chargement de page (puis réutilisés par chaque scène). */
let drawn: ReturnType<typeof drawAll> | null = null;

function drawAll() {
  return {
    modules: Object.fromEntries(MODULE_TYPES.map((type) => [type, levelsOf(type).map((level) => moduleArt(type, level))])) as Record<
      ModuleType,
      ReturnType<typeof moduleArt>[]
    >,
    ships: Object.fromEntries(SHIP_CLASSES.map((shipClass) => [shipClass, shipArt(shipClass)])) as Record<ShipClass, ReturnType<typeof shipArt>>,
    highlights: HIGHLIGHTS.map((kind) => [kind, highlightArt(kind)] as const),
    signs: SIGNS.map((kind) => [kind, signArt(kind)] as const),
    dots: NEED_KINDS.map((kind) => [kind, needDotArt(kind)] as const),
    flames: [flameArt(0), flameArt(1)] as const,
    scaffold: scaffoldArt(),
    bracket: bracketArt(72, 44),
    puff: puffArt(),
    pixel: pixelArt(),
    stars: starsArt(256),
    sun: sunArt(),
    skies: (["day", "night"] as const).map((kind) => [kind, skyArt(kind)] as const),
    wear: (["worn", "critical"] as const).flatMap((level) =>
      Array.from({ length: WEAR_SEGMENTS + 1 }, (_, filled) => [level, filled, wearArt(level, filled)] as const),
    ),
    font: fontArt(COLORS.good),
  };
}

/**
 * La planète, dessinée à la largeur de l'écran (un pixel de planète par pixel CSS, comme les sprites) :
 * elle ne laisse aucun bord coupé. Renvoie le nom de sa texture.
 */
export function registerPlanet(textures: Phaser.Textures.TextureManager, kind: PlanetKind, width: number): string {
  const key = TEX.planet(kind, width);
  if (!textures.exists(key)) textures.addCanvas(key, planetArt(kind, width, PLANET_HEIGHT).toCanvas());
  return key;
}

/** La limite d'une zone, dessinée à la demande (son losange grandit avec les anneaux). Renvoie sa texture. */
export function registerZoneEdge(textures: Phaser.Textures.TextureManager, kind: ZoneEdgeKind, ring: number): string {
  const key = TEX.zoneEdge(kind, ring);
  if (!textures.exists(key)) textures.addCanvas(key, zoneEdgeArt(kind, ring).toCanvas());
  return key;
}

/** Hauteur de la planète, en pixels d'art. */
export const PLANET_HEIGHT = 160;

/** Confie tous les sprites au gestionnaire de textures d'un jeu Phaser. */
export function registerArt(textures: Phaser.Textures.TextureManager): ArtBook {
  drawn ??= drawAll();
  const art = drawn;
  const add = (key: string, canvas: PixelCanvas) => {
    if (!textures.exists(key)) textures.addCanvas(key, canvas.toCanvas());
  };

  for (const type of MODULE_TYPES) {
    art.modules[type].forEach((sprite, index) => {
      add(TEX.module(type, index + 1), sprite.body);
      if (sprite.lights) add(TEX.lights(type, index + 1), sprite.lights);
    });
  }
  for (const shipClass of SHIP_CLASSES) {
    add(TEX.ship(shipClass), art.ships[shipClass].body);
    add(TEX.shipLights(shipClass), art.ships[shipClass].lights);
  }
  for (const [kind, canvas] of art.highlights) add(TEX.highlight(kind), canvas);
  for (const [kind, canvas] of art.signs) add(TEX.sign(kind), canvas);
  for (const [kind, canvas] of art.dots) add(TEX.dot(kind), canvas);
  add(TEX.flame(0), art.flames[0]);
  add(TEX.flame(1), art.flames[1]);
  add(TEX.scaffold, art.scaffold);
  add(TEX.bracket, art.bracket);
  add(TEX.puff, art.puff);
  add(TEX.pixel, art.pixel);
  add(TEX.stars, art.stars);
  add(TEX.sun, art.sun);
  for (const [kind, canvas] of art.skies) add(TEX.sky(kind), canvas);
  for (const [level, filled, canvas] of art.wear) add(TEX.wear(level, filled), canvas);
  if (!textures.exists(TEX.font)) {
    const font = textures.addCanvas(TEX.font, art.font.toCanvas());
    [...FONT_CHARS].forEach((char, index) => font?.add(char, 0, index * GLYPH_W, 0, GLYPH_W, GLYPH_H));
  }

  return {
    modules: Object.fromEntries(
      MODULE_TYPES.map((type) => [
        type,
        art.modules[type].map((sprite) => ({
          mask: sprite.body,
          height: MODULE_ANCHOR.y - firstOpaqueRow(sprite.body),
          hasLights: sprite.lights !== null,
        })),
      ]),
    ) as Record<ModuleType, ModuleSpriteInfo[]>,
    ships: Object.fromEntries(
      SHIP_CLASSES.map((shipClass) => {
        const ship = art.ships[shipClass];
        return [shipClass, { mask: ship.body, width: ship.body.width, height: ship.body.height, nozzle: ship.nozzle }];
      }),
    ) as Record<ShipClass, ShipSpriteInfo>,
  };
}
